import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Label,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  TabContent,
  TabPane,
  Table,
  Input,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import ClientAnamnesisOffcanvas from "../../../../Components/Studio/ClientAnamnesisOffcanvas";
import ClientClinicalProtocolsPanel from "../../../../Components/Studio/ClientClinicalProtocolsPanel";
import ClientImageMarketingConsentCard from "../../../../Components/Studio/ClientImageMarketingConsentCard";
import ClientMergeModal from "../../../../Components/Studio/ClientMergeModal";
import ClientTagsEditor from "../../../../Components/Studio/ClientTagsEditor";
import { hasClientServicePreferences } from "../../../../helpers/clientServicePreferences";
import {
  Client,
  ClientServiceHistoryEntry,
  deleteClient,
  getClientById,
  getClientServiceHistory,
} from "../../../../services/clientService";
import {
  getClientCredits,
  getClientCreditTransactions,
  getClientLoyalty,
} from "../../../../services/servicePackageService";
import { formatMoneyFromCents } from "../../../../helpers/money";
import {
  cancelClientSubscription,
  changeClientSubscriptionPlan,
  ClientSubscription,
  ClientSubscriptionPlan,
  createClientClubShareLink,
  createClientSubscription,
  getClientSubscriptionPlans,
  getClientSubscriptions,
  inviteClientClubWhatsApp,
} from "../../../../services/clientSubscriptionService";
import {
  ClientCredit,
  ClientLoyaltyProgress,
  CreditMovementType,
  CreditTransaction,
} from "../../../../types/servicePackage";
import { createClientLoyaltyShareLink } from "../../../../services/loyaltyCardService";
import { PaginationMeta } from "../../../../types/api";


const PER_PAGE = 15;

const formatPhoneLabel = (client: Client, fallback: string): string => {
  const digits = client.phone_number?.replace(/\D/g, "") ?? "";
  return digits ? `+${client.country_code} ${digits}` : fallback;
};

const formatSecondaryPhoneLabel = (client: Client): string | null => {
  const digits = client.secondary_phone_number?.replace(/\D/g, "") ?? "";
  if (!digits) {
    return null;
  }
  const country = client.secondary_country_code || client.country_code || "55";
  return `+${country} ${digits}`;
};

const formatDateLabel = (value: string | null | undefined, locale: string): string => {
  if (!value) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
    new Date(`${value}T00:00:00`)
  );
};

const ClientDetail = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId } = useParams<{ clientId: string }>();
  const parsedClientId = Number(clientId);
  const focusFromUrl = useRef(searchParams.get("focus") === "image_marketing");
  const [highlightImageMarketing] = useState(() => focusFromUrl.current);

  document.title = t("clients.detail.pageTitle");

  const [activeTab, setActiveTab] = useState("serviceHistory");
  const [client, setClient] = useState<Client | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);
  const [history, setHistory] = useState<ClientServiceHistoryEntry[]>([]);
  const [credits, setCredits] = useState<ClientCredit[]>([]);
  const [loyaltyProgress, setLoyaltyProgress] = useState<ClientLoyaltyProgress[]>([]);
  const [loyaltyShareUrl, setLoyaltyShareUrl] = useState<string | null>(null);
  const [loyaltyShareQr, setLoyaltyShareQr] = useState<string | null>(null);
  const [sharingLoyaltyLink, setSharingLoyaltyLink] = useState(false);
  const [clubSubscriptions, setClubSubscriptions] = useState<ClientSubscription[]>([]);
  const [clubPlans, setClubPlans] = useState<ClientSubscriptionPlan[]>([]);
  const [selectedClubPlanId, setSelectedClubPlanId] = useState("");
  const [changeClubPlanId, setChangeClubPlanId] = useState("");
  const [clubShareUrl, setClubShareUrl] = useState<string | null>(null);
  const [clubShareQr, setClubShareQr] = useState<string | null>(null);
  const [sharingClubLink, setSharingClubLink] = useState(false);
  const [invitingClubWhatsApp, setInvitingClubWhatsApp] = useState(false);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [creditsMeta, setCreditsMeta] = useState<PaginationMeta | null>(null);
  const [transactionsMeta, setTransactionsMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [creditsPage, setCreditsPage] = useState(1);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [movementTypeFilter, setMovementTypeFilter] = useState("");
  const [fromDateFilter, setFromDateFilter] = useState("");
  const [toDateFilter, setToDateFilter] = useState("");
  const [loadingClient, setLoadingClient] = useState(true);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  const [loadingClub, setLoadingClub] = useState(false);
  const [savingClub, setSavingClub] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [anamnesisOpen, setAnamnesisOpen] = useState(false);

  const notAvailable = t("clients.common.notAvailable");

  const formatDateTime = useCallback(
    (value?: string | null) => {
      if (!value) {
        return notAvailable;
      }

      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, notAvailable]
  );

  const formatDate = useCallback(
    (value?: string | null) => {
      if (!value) {
        return notAvailable;
      }

      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
      }).format(new Date(value));
    },
    [i18n.language, notAvailable]
  );

  const loadClient = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      navigate("/clients");
      return;
    }

    setLoadingClient(true);

    try {
      const response = await getClientById(parsedClientId);
      setClient(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("clients.toast.loadError"));
      navigate("/clients");
    } finally {
      setLoadingClient(false);
    }
  }, [navigate, parsedClientId, t]);

  const loadServiceHistory = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    setLoadingHistory(true);

    try {
      const response = await getClientServiceHistory(parsedClientId, {
        page,
        per_page: PER_PAGE,
      });
      setHistory(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("clients.serviceHistory.toast.loadError"));
    } finally {
      setLoadingHistory(false);
    }
  }, [page, parsedClientId, t]);

  const loadCredits = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    setLoadingCredits(true);

    try {
      const response = await getClientCredits(parsedClientId, {
        page: creditsPage,
        per_page: PER_PAGE,
      });
      setCredits(response.data);
      setCreditsMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("clients.credits.toast.loadError"));
    } finally {
      setLoadingCredits(false);
    }
  }, [creditsPage, parsedClientId, t]);

  const loadLoyalty = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    setLoadingLoyalty(true);

    try {
      const response = await getClientLoyalty(parsedClientId);
      setLoyaltyProgress(response.data ?? []);
    } catch (error: any) {
      toast.error(error?.data || t("clients.loyalty.toast.loadError"));
    } finally {
      setLoadingLoyalty(false);
    }
  }, [parsedClientId, t]);

  const handleShareLoyaltyLink = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    setSharingLoyaltyLink(true);
    try {
      const response = await createClientLoyaltyShareLink(parsedClientId);
      setLoyaltyShareUrl(response.data?.url ?? null);
      setLoyaltyShareQr(response.data?.qr_data_uri ?? null);
      toast.success(t("clients.loyalty.toast.shareReady"));
    } catch (error: any) {
      toast.error(error?.data || t("clients.loyalty.toast.shareError"));
    } finally {
      setSharingLoyaltyLink(false);
    }
  }, [parsedClientId, t]);

  const confirmDeleteClient = useCallback(async () => {
    if (!client) {
      return;
    }

    setDeletingClient(true);

    try {
      const response = await deleteClient(client.id);
      toast.success(response.message || t("clients.toast.deleteSuccess"));
      setDeleteModalOpen(false);
      navigate("/clients");
    } catch (error: any) {
      toast.error(error?.data || t("clients.toast.deleteError"));
    } finally {
      setDeletingClient(false);
    }
  }, [client, navigate, t]);

  const copyLoyaltyShareUrl = useCallback(async () => {
    if (!loyaltyShareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(loyaltyShareUrl);
      toast.success(t("clients.loyalty.toast.copied"));
    } catch {
      toast.error(t("clients.loyalty.toast.copyError"));
    }
  }, [loyaltyShareUrl, t]);

  const loadClub = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    setLoadingClub(true);

    try {
      const [subsResponse, plansResponse] = await Promise.all([
        getClientSubscriptions(parsedClientId),
        getClientSubscriptionPlans({ per_page: 100, is_active: true }),
      ]);
      setClubSubscriptions(subsResponse.data ?? []);
      setClubPlans(plansResponse.data ?? []);
    } catch (error: any) {
      toast.error(error?.data || t("clients.club.toast.loadError"));
    } finally {
      setLoadingClub(false);
    }
  }, [parsedClientId, t]);

  const loadTransactions = useCallback(async () => {
    if (!Number.isFinite(parsedClientId) || parsedClientId <= 0) {
      return;
    }

    if (fromDateFilter && toDateFilter && fromDateFilter > toDateFilter) {
      toast.error(t("clients.creditStatement.filter.dateRangeInvalid"));
      return;
    }

    setLoadingTransactions(true);

    try {
      const response = await getClientCreditTransactions(parsedClientId, {
        page: transactionsPage,
        per_page: PER_PAGE,
        ...(movementTypeFilter
          ? { movement_type: movementTypeFilter }
          : {}),
        ...(fromDateFilter ? { from_date: fromDateFilter } : {}),
        ...(toDateFilter ? { to_date: toDateFilter } : {}),
      });
      setTransactions(response.data);
      setTransactionsMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("clients.creditStatement.toast.loadError"));
    } finally {
      setLoadingTransactions(false);
    }
  }, [
    fromDateFilter,
    movementTypeFilter,
    parsedClientId,
    t,
    toDateFilter,
    transactionsPage,
  ]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  useEffect(() => {
    if (!highlightImageMarketing) {
      return;
    }

    if (searchParams.get("focus") === "image_marketing") {
      const next = new URLSearchParams(searchParams);
      next.delete("focus");
      setSearchParams(next, { replace: true });
    }

    if (loadingClient || !client) {
      return;
    }

    const timer = window.setTimeout(() => {
      document
        .getElementById("client-image-marketing-consent")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [client, highlightImageMarketing, loadingClient, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeTab === "serviceHistory") {
      loadServiceHistory();
    }
  }, [activeTab, loadServiceHistory]);

  useEffect(() => {
    if (activeTab === "credits") {
      loadCredits();
    }
  }, [activeTab, loadCredits]);

  useEffect(() => {
    if (activeTab === "loyalty") {
      loadLoyalty();
    }
  }, [activeTab, loadLoyalty]);

  useEffect(() => {
    if (activeTab === "club") {
      void loadClub();
    }
  }, [activeTab, loadClub]);

  useEffect(() => {
    if (activeTab === "creditStatement") {
      loadTransactions();
    }
  }, [activeTab, loadTransactions]);

  const resolveMovementLabel = (movementType: CreditMovementType): string =>
    t(`clients.creditStatement.movement.${movementType}`);

  const formatSessionsDelta = (delta: number): string => {
    if (delta > 0) {
      return `+${delta}`;
    }

    return String(delta);
  };

  const paginationLastPage = meta?.last_page ?? 1;
  const paginationFrom = meta && meta.total > 0 ? (meta.current_page - 1) * meta.per_page + 1 : 0;
  const paginationTo = meta
    ? Math.min(meta.current_page * meta.per_page, meta.total)
    : 0;

  const creditsLastPage = creditsMeta?.last_page ?? 1;
  const creditsFrom =
    creditsMeta && creditsMeta.total > 0
      ? (creditsMeta.current_page - 1) * creditsMeta.per_page + 1
      : 0;
  const creditsTo = creditsMeta
    ? Math.min(creditsMeta.current_page * creditsMeta.per_page, creditsMeta.total)
    : 0;

  const transactionsLastPage = transactionsMeta?.last_page ?? 1;
  const transactionsFrom =
    transactionsMeta && transactionsMeta.total > 0
      ? (transactionsMeta.current_page - 1) * transactionsMeta.per_page + 1
      : 0;
  const transactionsTo = transactionsMeta
    ? Math.min(
        transactionsMeta.current_page * transactionsMeta.per_page,
        transactionsMeta.total
      )
    : 0;

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("clients.detail.breadcrumb.title")}
          pageTitle={client?.name ?? t("clients.detail.breadcrumb.pageTitle")}
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div>
                  {loadingClient ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h4 className="card-title mb-0">{client?.name}</h4>
                        {client?.email_suppressed_at ? (
                          <Badge color="warning" className="text-uppercase">
                            {t("clients.detail.emailSuppressedBadge")}
                          </Badge>
                        ) : null}
                      </div>
                      <p className="text-muted mb-0">
                        {(() => {
                          if (!client) {
                            return notAvailable;
                          }
                          const secondary = formatSecondaryPhoneLabel(client);
                          const primary = formatPhoneLabel(client, notAvailable);
                          return secondary
                            ? `${primary} · ${t("clients.fields.secondary_phone.short")}: ${secondary}`
                            : primary;
                        })()}
                      </p>
                      {client ? (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {client.birth_date ? (
                            <Badge color="light" className="text-body">
                              {t("studio.clients.fields.birth_date")}:{" "}
                              {formatDateLabel(client.birth_date, i18n.language)}
                            </Badge>
                          ) : null}
                          {client.gender ? (
                            <Badge color="light" className="text-body">
                              {t(`clients.fields.gender.${client.gender}`)}
                            </Badge>
                          ) : null}
                          {client.instagram_handle ? (
                            <a
                              href={`https://instagram.com/${client.instagram_handle}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-decoration-none"
                            >
                              <Badge color="light" className="text-body">
                                <i className="ri-instagram-line me-1" aria-hidden />@
                                {client.instagram_handle}
                              </Badge>
                            </a>
                          ) : null}
                          {client.acquisition_source ? (
                            <Badge color="light" className="text-body">
                              {t("clients.fields.acquisition_source.short")}:{" "}
                              {client.acquisition_source}
                            </Badge>
                          ) : null}
                          {client.allow_online_booking === false ? (
                            <Badge color="secondary">
                              {t("clients.detail.onlineBookingBlocked")}
                            </Badge>
                          ) : null}
                          {client.appointment_email_opt_out ? (
                            <Badge color="warning" className="text-dark">
                              {t("clients.detail.optOut.appointmentEmail")}
                            </Badge>
                          ) : null}
                          {client.marketing_email_opt_out ? (
                            <Badge color="warning" className="text-dark">
                              {t("clients.detail.optOut.marketingEmail")}
                            </Badge>
                          ) : null}
                          {client.sms_marketing_opt_out ? (
                            <Badge color="warning" className="text-dark">
                              {t("clients.detail.optOut.sms")}
                            </Badge>
                          ) : null}
                          {client.source_registered_on ? (
                            <Badge color="light" className="text-body">
                              {t("clients.fields.source_registered_on.short")}:{" "}
                              {formatDateLabel(client.source_registered_on, i18n.language)}
                            </Badge>
                          ) : null}
                        </div>
                      ) : null}
                      {client?.email_suppressed_at ? (
                        <p className="text-warning small mb-0 mt-2">
                          {t("clients.detail.emailSuppressedHint")}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {client ? (
                    <>
                      <Button
                        tag={Link}
                        to={`/orders?client_id=${client.id}`}
                        color="soft-warning"
                        size="sm"
                      >
                        <i className="ri-file-list-3-line align-bottom me-1" />
                        {t("clients.actions.new_order")}
                      </Button>
                      <Button
                        tag={Link}
                        to={`/agenda?client_id=${client.id}`}
                        color="soft-primary"
                        size="sm"
                      >
                        <i className="ri-calendar-line align-bottom me-1" />
                        {t("clients.actions.schedule")}
                      </Button>
                      <Button
                        color="soft-success"
                        size="sm"
                        onClick={() => setAnamnesisOpen(true)}
                      >
                        <i className="ri-health-book-line align-bottom me-1" />
                        {t("clients.actions.new_anamnesis")}
                      </Button>
                      <Button
                        color="soft-secondary"
                        size="sm"
                        onClick={() => setMergeModalOpen(true)}
                      >
                        <i className="ri-merge-cells-horizontal align-bottom me-1" />
                        {t("clients.merge.button")}
                      </Button>
                      <Button
                        color="soft-danger"
                        size="sm"
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        <i className="ri-delete-bin-line align-bottom me-1" />
                        {t("clients.actions.delete")}
                      </Button>
                    </>
                  ) : null}
                  <Button tag={Link} to="/clients" color="light" size="sm">
                    {t("clients.detail.actions.backToList")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {!loadingClient && client ? (
                  <div className="mb-3">
                    <h6 className="fs-13 mb-1">{t("clients.tags.sectionTitle")}</h6>
                    <p className="text-muted small mb-2">{t("clients.tags.sectionHint")}</p>
                    <ClientTagsEditor
                      clientId={client.id}
                      initialTags={client.tags}
                      onChanged={(tags) =>
                        setClient((current) => (current ? { ...current, tags } : current))
                      }
                    />
                  </div>
                ) : null}

                {!loadingClient && client ? (
                  <ClientImageMarketingConsentCard
                    clientId={client.id}
                    highlight={highlightImageMarketing}
                    onChanged={(state) =>
                      setClient((current) =>
                        current
                          ? {
                              ...current,
                              image_marketing_consent_status: state.status,
                              can_use_in_marketing: state.can_use_in_marketing,
                            }
                          : current
                      )
                    }
                  />
                ) : null}

                {!loadingClient && client?.notes?.trim() ? (
                  <Alert color="info" className="border-0 bg-info-subtle mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <i
                        className="ri-sticky-note-line fs-18 text-info mt-1 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <h6 className="alert-heading fs-13 mb-0">
                            {t("clients.detail.notesLabel")}
                          </h6>
                          <Button
                            tag={Link}
                            to={`/clients?edit=${client.id}`}
                            color="soft-primary"
                            size="sm"
                          >
                            <i className="ri-pencil-line align-bottom me-1" />
                            {t("clients.detail.notesEditCta")}
                          </Button>
                        </div>
                        <p
                          className="mb-0 text-body"
                          style={{ whiteSpace: "pre-wrap" }}
                          data-clarity-mask="true"
                        >
                          {client.notes.trim()}
                        </p>
                      </div>
                    </div>
                  </Alert>
                ) : !loadingClient && client ? (
                  <Alert color="light" className="border mb-3">
                    <p className="text-muted mb-2">{t("clients.detail.notesEmpty")}</p>
                    <Button
                      tag={Link}
                      to={`/clients?edit=${client.id}`}
                      color="soft-primary"
                      size="sm"
                    >
                      <i className="ri-pencil-line align-bottom me-1" />
                      {t("clients.detail.notesEmptyCta")}
                    </Button>
                  </Alert>
                ) : null}

                {!loadingClient &&
                client &&
                hasClientServicePreferences(client.service_preferences) ? (
                  <Alert color="primary" className="border-0 bg-primary-subtle mb-3">
                    <div className="d-flex align-items-start gap-2">
                      <i
                        className="ri-sparkling-2-line fs-18 text-primary mt-1 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <h6 className="alert-heading fs-13 mb-0">
                            {t("clients.preferences.sectionTitle")}
                          </h6>
                          <Button
                            tag={Link}
                            to={`/clients?edit=${client.id}`}
                            color="soft-primary"
                            size="sm"
                          >
                            <i className="ri-pencil-line align-bottom me-1" />
                            {t("clients.preferences.editCta")}
                          </Button>
                        </div>
                        <ul className="mb-0 ps-3" data-clarity-mask="true">
                          {client.service_preferences?.preferred_professional_id ? (
                            <li>
                              {t("clients.preferences.preferredProfessional")}: #
                              {client.service_preferences.preferred_professional_id}
                            </li>
                          ) : null}
                          {client.service_preferences?.haircut_style ? (
                            <li>
                              {t("clients.preferences.haircutStyle")}:{" "}
                              {client.service_preferences.haircut_style}
                            </li>
                          ) : null}
                          {client.service_preferences?.guard ? (
                            <li>
                              {t("clients.preferences.guard")}:{" "}
                              {client.service_preferences.guard}
                            </li>
                          ) : null}
                          {client.service_preferences?.beard_notes ? (
                            <li>
                              {t("clients.preferences.beardNotes")}:{" "}
                              {client.service_preferences.beard_notes}
                            </li>
                          ) : null}
                          {client.service_preferences?.post_care_notes ? (
                            <li>
                              {t("clients.preferences.postCareNotes")}:{" "}
                              {client.service_preferences.post_care_notes}
                            </li>
                          ) : null}
                          {client.service_preferences?.protocol_alert ? (
                            <li>
                              {t("clients.preferences.protocolAlert")}:{" "}
                              {client.service_preferences.protocol_alert}
                            </li>
                          ) : null}
                        </ul>
                      </div>
                    </div>
                  </Alert>
                ) : !loadingClient && client ? (
                  <Alert color="light" className="border mb-3">
                    <p className="text-muted mb-2">{t("clients.preferences.empty")}</p>
                    <Button
                      tag={Link}
                      to={`/clients?edit=${client.id}`}
                      color="soft-primary"
                      size="sm"
                    >
                      <i className="ri-sparkling-2-line align-bottom me-1" />
                      {t("clients.preferences.emptyCta")}
                    </Button>
                  </Alert>
                ) : null}

                <Nav tabs className="nav-tabs-custom nav-primary mb-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === "serviceHistory" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("serviceHistory");
                      }}
                    >
                      {t("clients.tabs.serviceHistory")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "loyalty" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("loyalty");
                      }}
                    >
                      {t("clients.tabs.loyalty")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "club" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("club");
                      }}
                    >
                      {t("clients.tabs.club")}
                      {client?.club_summary?.status ? (
                        <Badge
                          color={
                            client.club_summary.status === "active"
                              ? "success"
                              : client.club_summary.status === "past_due"
                                ? "warning"
                                : "secondary"
                          }
                          className="ms-2"
                        >
                          {t(`clients.club.status.${client.club_summary.status}`)}
                        </Badge>
                      ) : null}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "credits" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("credits");
                      }}
                    >
                      {t("clients.tabs.credits")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "creditStatement" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("creditStatement");
                      }}
                    >
                      {t("clients.tabs.creditStatement")}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "protocols" ? "active" : ""}
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        setActiveTab("protocols");
                      }}
                    >
                      {t("clients.tabs.protocols")}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="serviceHistory">
                    {loadingHistory ? (
                      <div className="text-center py-4">
                        <Spinner color="primary">{t("clients.loading")}</Spinner>
                      </div>
                    ) : history.length === 0 ? (
                      <p className="text-muted mb-0">{t("clients.serviceHistory.empty")}</p>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table className="align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{t("clients.serviceHistory.table.date")}</th>
                                <th>{t("clients.serviceHistory.table.service")}</th>
                                <th>{t("clients.serviceHistory.table.professional")}</th>
                                <th>{t("clients.serviceHistory.table.notes")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {history.map((entry) => (
                                <tr key={entry.id}>
                                  <td>{formatDateTime(entry.order?.created_at)}</td>
                                  <td>{entry.service?.name ?? notAvailable}</td>
                                  <td>{entry.professional?.name ?? notAvailable}</td>
                                  <td>{entry.execution_notes}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {meta && meta.total > PER_PAGE && (
                          <Row className="align-items-center mt-3">
                            <Col sm={6}>
                              <p className="text-muted mb-0">
                                {t("clients.pagination.showing", {
                                  from: paginationFrom,
                                  to: paginationTo,
                                  total: meta.total,
                                })}
                              </p>
                            </Col>
                            <Col sm={6}>
                              <Pagination className="pagination-separated justify-content-sm-end mb-0">
                                <PaginationItem disabled={page <= 1}>
                                  <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (page > 1) {
                                        setPage(page - 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.previous")}
                                  </PaginationLink>
                                </PaginationItem>
                                <PaginationItem disabled={page >= paginationLastPage}>
                                  <PaginationLink
                                    next
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (page < paginationLastPage) {
                                        setPage(page + 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.next")}
                                  </PaginationLink>
                                </PaginationItem>
                              </Pagination>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </TabPane>

                  <TabPane tabId="loyalty">
                    {loadingLoyalty ? (
                      <div className="text-center py-4">
                        <Spinner color="primary">{t("clients.loading")}</Spinner>
                      </div>
                    ) : loyaltyProgress.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted mb-2">{t("clients.loyalty.empty")}</p>
                        <p className="text-muted small mb-3">
                          {t("clients.loyalty.emptyHint")}
                        </p>
                        <Button color="primary" outline size="sm" tag={Link} to="/services">
                          {t("clients.loyalty.goServices")}
                        </Button>
                      </div>
                    ) : (
                      <div className="d-flex flex-column gap-3">
                        <div className="d-flex justify-content-end mb-2">
                          <Button
                            color="soft-primary"
                            size="sm"
                            disabled={sharingLoyaltyLink}
                            onClick={() => void handleShareLoyaltyLink()}
                          >
                            {sharingLoyaltyLink ? (
                              <Spinner size="sm" />
                            ) : (
                              t("clients.loyalty.shareQr")
                            )}
                          </Button>
                        </div>
                        {loyaltyShareUrl ? (
                          <Alert color="info" className="mb-3">
                            <p className="mb-2 small">{t("clients.loyalty.shareHint")}</p>
                            <div className="d-flex flex-wrap align-items-start gap-3 mb-2">
                              {loyaltyShareQr ? (
                                <div className="text-center">
                                  <img
                                    src={loyaltyShareQr}
                                    alt={t("clients.loyalty.qrAlt")}
                                    width={140}
                                    height={140}
                                    className="border rounded bg-white p-1"
                                  />
                                  <p className="small text-muted mb-0 mt-1">
                                    {t("clients.loyalty.qrCaption")}
                                  </p>
                                </div>
                              ) : null}
                              <div className="flex-grow-1" style={{ minWidth: 180 }}>
                                <code className="small d-block text-break mb-2">
                                  {loyaltyShareUrl}
                                </code>
                                <Button
                                  color="primary"
                                  size="sm"
                                  outline
                                  onClick={() => void copyLoyaltyShareUrl()}
                                >
                                  {t("clients.loyalty.copyLink")}
                                </Button>
                              </div>
                            </div>
                          </Alert>
                        ) : null}
                        {loyaltyProgress.map((card) => {
                          const required = card.stamps_required ?? 0;
                          const count = card.stamps_count;
                          const percent =
                            required > 0
                              ? Math.min(100, Math.round((count / required) * 100))
                              : 0;
                          const label = card.card_name || card.service_name;

                          return (
                            <div
                              key={
                                card.loyalty_card_id
                                  ? `card-${card.loyalty_card_id}`
                                  : `service-${card.service_id}`
                              }
                              className="border rounded p-3"
                            >
                              <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                <div>
                                  <h6 className="mb-1">{label}</h6>
                                  {card.kind === "card" ? (
                                    <p className="text-muted small mb-0">
                                      {t("clients.loyalty.multiServiceHint")}
                                    </p>
                                  ) : null}
                                  <p className="text-muted small mb-0">
                                    {t("clients.loyalty.progressLabel", {
                                      count,
                                      required,
                                    })}
                                  </p>
                                </div>
                                {card.free_credits > 0 ? (
                                  <Badge color="soft-success" className="fw-normal align-self-start">
                                    {t("clients.loyalty.freeCredits", {
                                      count: card.free_credits,
                                    })}
                                  </Badge>
                                ) : null}
                              </div>
                              <div
                                className="progress"
                                style={{ height: "0.5rem" }}
                                aria-label={t("clients.loyalty.progressA11y", {
                                  count,
                                  required,
                                })}
                              >
                                <div
                                  className="progress-bar bg-success"
                                  role="progressbar"
                                  style={{ width: `${percent}%` }}
                                  aria-valuenow={count}
                                  aria-valuemin={0}
                                  aria-valuemax={required}
                                />
                              </div>
                              <p
                                className={`small mb-0 mt-2 ${
                                  card.remaining_to_reward != null &&
                                  card.remaining_to_reward > 0 &&
                                  card.remaining_to_reward <= 2
                                    ? "text-warning fw-medium"
                                    : "text-muted"
                                }`}
                              >
                                {card.remaining_to_reward != null &&
                                card.remaining_to_reward > 0
                                  ? card.remaining_to_reward <= 2
                                    ? t("clients.loyalty.remainingAlmost", {
                                        count: card.remaining_to_reward,
                                      })
                                    : t("clients.loyalty.remaining", {
                                        count: card.remaining_to_reward,
                                      })
                                  : t("clients.loyalty.ready")}
                              </p>
                              {card.reward_validity_days != null ? (
                                <p className="small text-muted mb-0 mt-1">
                                  {t("clients.loyalty.validityHint", {
                                    days: card.reward_validity_days,
                                  })}
                                </p>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabPane>

                  <TabPane tabId="club">
                    {loadingClub ? (
                      <div className="text-center py-4">
                        <Spinner color="primary">{t("clients.loading")}</Spinner>
                      </div>
                    ) : (
                      <>
                        {(client?.club_summary?.status === "past_due" ||
                          client?.club_summary?.status === "suspended") && (
                          <Alert color="warning" className="mb-3">
                            {t("clients.club.banner.inadimplent", {
                              plan: client.club_summary.plan_name ?? "",
                              status: t(
                                `clients.club.status.${client.club_summary.status}`
                              ),
                            })}
                          </Alert>
                        )}

                        {client?.club_summary?.has_active &&
                        client.club_summary.visits_limit != null ? (
                          <Alert color="info" className="mb-3">
                            {t("clients.club.visitsUsed", {
                              used: client.club_summary.visits_used ?? 0,
                              limit: client.club_summary.visits_limit,
                            })}
                          </Alert>
                        ) : client?.club_summary?.has_active &&
                          client.club_summary.visits_used != null ? (
                          <Alert color="info" className="mb-3">
                            {t("clients.club.visitsUsedUnlimited", {
                              used: client.club_summary.visits_used,
                            })}
                          </Alert>
                        ) : null}

                        {client?.club_summary?.has_active ||
                        client?.club_summary?.price_locked_cents != null ? (
                          <div className="mb-3">
                            {client.club_summary.price_locked_cents != null ? (
                              <p className="text-muted small mb-1">
                                {t("clients.club.lockedPrice", {
                                  price: formatMoneyFromCents(
                                    client.club_summary.price_locked_cents,
                                    i18n.language
                                  ),
                                })}
                              </p>
                            ) : null}
                            {(client.club_summary.proration_credit_cents ?? 0) >
                            0 ? (
                              <Alert color="success" className="mb-2 py-2">
                                {t("clients.club.prorationCredit", {
                                  credit: formatMoneyFromCents(
                                    client.club_summary.proration_credit_cents ??
                                      0,
                                    i18n.language
                                  ),
                                })}
                              </Alert>
                            ) : null}
                            {client.club_summary.has_active ? (
                              <>
                                <h6 className="mb-2">
                                  {t("clients.club.visitsHistoryTitle")}
                                </h6>
                                {(client.club_summary.visit_dates?.length ??
                                  0) === 0 ? (
                                  <p className="text-muted small mb-0">
                                    {t("clients.club.visitsHistoryEmpty")}
                                  </p>
                                ) : (
                                  <ul className="mb-0 ps-3">
                                    {(client.club_summary.visit_dates ?? []).map(
                                      (date) => (
                                        <li key={date}>
                                          {formatDateLabel(date, i18n.language)}
                                        </li>
                                      )
                                    )}
                                  </ul>
                                )}
                              </>
                            ) : null}
                          </div>
                        ) : null}

                        {clubSubscriptions.length === 0 ? (
                          <p className="text-muted mb-3">{t("clients.club.empty")}</p>
                        ) : (
                          <div className="table-responsive mb-3">
                            <Table className="align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>{t("clients.club.table.plan")}</th>
                                  <th>{t("clients.club.table.status")}</th>
                                  <th>{t("clients.club.table.periodEnd")}</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {clubSubscriptions.map((subscription) => (
                                  <tr key={subscription.id}>
                                    <td>{subscription.plan?.name ?? "—"}</td>
                                    <td>
                                      <Badge
                                        color={
                                          subscription.status === "active"
                                            ? "success"
                                            : subscription.status === "past_due"
                                              ? "warning"
                                              : "secondary"
                                        }
                                      >
                                        {t(
                                          `clients.club.status.${subscription.status}`
                                        )}
                                      </Badge>
                                    </td>
                                    <td>
                                      {formatDateTime(
                                        subscription.current_period_end
                                      )}
                                    </td>
                                    <td className="text-end">
                                      {subscription.status !== "canceled" ? (
                                        <Button
                                          color="soft-danger"
                                          size="sm"
                                          disabled={savingClub}
                                          onClick={async () => {
                                            setSavingClub(true);
                                            try {
                                              await cancelClientSubscription(
                                                parsedClientId,
                                                subscription.id,
                                                true
                                              );
                                              toast.success(
                                                t("clients.club.toast.canceled")
                                              );
                                              await loadClub();
                                              const refreshed =
                                                await getClientById(
                                                  parsedClientId
                                                );
                                              setClient(refreshed.data);
                                            } catch (error: any) {
                                              toast.error(
                                                error?.data?.error?.message ||
                                                  error?.data ||
                                                  t(
                                                    "clients.club.toast.cancelError"
                                                  )
                                              );
                                            } finally {
                                              setSavingClub(false);
                                            }
                                          }}
                                        >
                                          {t("clients.club.actions.cancel")}
                                        </Button>
                                      ) : null}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}

                        {clubSubscriptions.some(
                          (s) =>
                            s.status === "active" || s.status === "past_due"
                        ) ? (
                          <div className="border rounded p-3 mb-3">
                            <h6 className="mb-2">
                              {t("clients.club.changePlanTitle")}
                            </h6>
                            <p className="text-muted small mb-3">
                              {t("clients.club.changePlanHint")}
                            </p>
                            <div className="d-flex flex-wrap gap-2 align-items-end">
                              <div style={{ minWidth: 220 }}>
                                <Label className="form-label">
                                  {t("clients.club.fields.plan")}
                                </Label>
                                <Input
                                  type="select"
                                  value={changeClubPlanId}
                                  onChange={(e) =>
                                    setChangeClubPlanId(e.target.value)
                                  }
                                >
                                  <option value="">
                                    {t("clients.club.fields.planPlaceholder")}
                                  </option>
                                  {clubPlans.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                      {plan.name}
                                    </option>
                                  ))}
                                </Input>
                              </div>
                              <Button
                                color="primary"
                                disabled={!changeClubPlanId || savingClub}
                                onClick={async () => {
                                  const activeSub = clubSubscriptions.find(
                                    (s) =>
                                      s.status === "active" ||
                                      s.status === "past_due"
                                  );
                                  if (!activeSub || !changeClubPlanId) {
                                    return;
                                  }
                                  setSavingClub(true);
                                  try {
                                    const changed =
                                      await changeClientSubscriptionPlan(
                                        parsedClientId,
                                        activeSub.id,
                                        Number(changeClubPlanId)
                                      );
                                    const proration =
                                      changed.data?.proration_cents ?? 0;
                                    if (proration > 0) {
                                      toast.success(
                                        t(
                                          "clients.club.toast.planChangedCharge",
                                          {
                                            amount: formatMoneyFromCents(
                                              proration,
                                              i18n.language
                                            ),
                                          }
                                        )
                                      );
                                    } else if (proration < 0) {
                                      toast.success(
                                        t(
                                          "clients.club.toast.planChangedCredit",
                                          {
                                            amount: formatMoneyFromCents(
                                              Math.abs(proration),
                                              i18n.language
                                            ),
                                          }
                                        )
                                      );
                                    } else {
                                      toast.success(
                                        t("clients.club.toast.planChanged")
                                      );
                                    }
                                    setChangeClubPlanId("");
                                    await loadClub();
                                    const refreshed = await getClientById(
                                      parsedClientId
                                    );
                                    setClient(refreshed.data);
                                  } catch (error: any) {
                                    toast.error(
                                      error?.data?.error?.message ||
                                        error?.data ||
                                        t("clients.club.toast.planChangeError")
                                    );
                                  } finally {
                                    setSavingClub(false);
                                  }
                                }}
                              >
                                {savingClub ? (
                                  <Spinner size="sm" />
                                ) : (
                                  t("clients.club.actions.changePlan")
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : null}

                        <div className="border rounded p-3 mb-3">
                          <h6 className="mb-2">
                            {t("clients.club.shareTitle")}
                          </h6>
                          <p className="text-muted small mb-3">
                            {t("clients.club.shareHint")}
                          </p>
                          <div className="d-flex flex-wrap gap-2 mb-2">
                            <Button
                              color="soft-primary"
                              disabled={sharingClubLink || invitingClubWhatsApp}
                              onClick={async () => {
                                setSharingClubLink(true);
                                try {
                                  const response =
                                    await createClientClubShareLink(
                                      parsedClientId,
                                      selectedClubPlanId
                                        ? Number(selectedClubPlanId)
                                        : null
                                    );
                                  setClubShareUrl(
                                    response.data?.url ?? null
                                  );
                                  setClubShareQr(
                                    response.data?.qr_data_uri ?? null
                                  );
                                  toast.success(
                                    t("clients.club.toast.shareCreated")
                                  );
                                } catch (error: any) {
                                  toast.error(
                                    error?.data?.error?.message ||
                                      t("clients.club.toast.shareError")
                                  );
                                } finally {
                                  setSharingClubLink(false);
                                }
                              }}
                            >
                              {sharingClubLink ? (
                                <Spinner size="sm" />
                              ) : (
                                t("clients.club.actions.shareLink")
                              )}
                            </Button>
                            {client?.phone_number ? (
                              <Button
                                color="success"
                                disabled={
                                  sharingClubLink || invitingClubWhatsApp
                                }
                                onClick={async () => {
                                  setInvitingClubWhatsApp(true);
                                  try {
                                    const response =
                                      await inviteClientClubWhatsApp(
                                        parsedClientId,
                                        selectedClubPlanId
                                          ? Number(selectedClubPlanId)
                                          : null
                                      );
                                    setClubShareUrl(
                                      response.data?.url ?? null
                                    );
                                    setClubShareQr(
                                      response.data?.qr_data_uri ?? null
                                    );

                                    if (response.data?.template_queued) {
                                      toast.success(
                                        t(
                                          "clients.club.toast.inviteTemplateQueued"
                                        )
                                      );
                                      return;
                                    }

                                    const waUrl = response.data?.wa_url;
                                    if (waUrl) {
                                      window.open(waUrl, "_blank", "noopener");
                                      toast.info(
                                        t(
                                          "clients.club.toast.inviteFreeTextFallback"
                                        )
                                      );
                                    } else {
                                      toast.error(
                                        t("clients.club.toast.inviteError")
                                      );
                                    }
                                  } catch (error: any) {
                                    toast.error(
                                      error?.data?.error?.message ||
                                        t("clients.club.toast.inviteError")
                                    );
                                  } finally {
                                    setInvitingClubWhatsApp(false);
                                  }
                                }}
                              >
                                {invitingClubWhatsApp ? (
                                  <Spinner size="sm" />
                                ) : (
                                  t("clients.club.actions.inviteWhatsapp")
                                )}
                              </Button>
                            ) : null}
                            {clubShareUrl ? (
                              <>
                                <Button
                                  color="soft-secondary"
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(
                                        clubShareUrl
                                      );
                                      toast.success(
                                        t("clients.club.toast.shareCopied")
                                      );
                                    } catch {
                                      toast.error(
                                        t("clients.club.toast.shareCopyError")
                                      );
                                    }
                                  }}
                                >
                                  {t("clients.club.actions.copyLink")}
                                </Button>
                                <Button
                                  color="soft-success"
                                  tag="a"
                                  href={clubShareUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {t("clients.club.actions.openLink")}
                                </Button>
                              </>
                            ) : null}
                          </div>
                          {clubShareUrl ? (
                            <div className="d-flex flex-wrap gap-3 align-items-start">
                              {clubShareQr ? (
                                <img
                                  src={clubShareQr}
                                  alt=""
                                  width={96}
                                  height={96}
                                />
                              ) : null}
                              <code className="small text-break">
                                {clubShareUrl}
                              </code>
                            </div>
                          ) : null}
                        </div>

                        <div className="border rounded p-3">
                          <h6 className="mb-2">
                            {t("clients.club.subscribeTitle")}
                          </h6>
                          <p className="text-muted small mb-3">
                            {t("clients.club.subscribeHint")}
                          </p>
                          <div className="d-flex flex-wrap gap-2 align-items-end">
                            <div style={{ minWidth: 220 }}>
                              <Label className="form-label">
                                {t("clients.club.fields.plan")}
                              </Label>
                              <Input
                                type="select"
                                value={selectedClubPlanId}
                                onChange={(e) =>
                                  setSelectedClubPlanId(e.target.value)
                                }
                              >
                                <option value="">
                                  {t("clients.club.fields.planPlaceholder")}
                                </option>
                                {clubPlans.map((plan) => (
                                  <option key={plan.id} value={plan.id}>
                                    {plan.name}
                                  </option>
                                ))}
                              </Input>
                            </div>
                            <Button
                              color="primary"
                              disabled={!selectedClubPlanId || savingClub}
                              onClick={async () => {
                                setSavingClub(true);
                                try {
                                  await createClientSubscription(
                                    parsedClientId,
                                    {
                                      client_subscription_plan_id: Number(
                                        selectedClubPlanId
                                      ),
                                      activate_manually: true,
                                    }
                                  );
                                  toast.success(
                                    t("clients.club.toast.subscribed")
                                  );
                                  setSelectedClubPlanId("");
                                  await loadClub();
                                  const refreshed = await getClientById(
                                    parsedClientId
                                  );
                                  setClient(refreshed.data);
                                } catch (error: any) {
                                  toast.error(
                                    error?.data?.error?.message ||
                                      error?.data ||
                                      t("clients.club.toast.subscribeError")
                                  );
                                } finally {
                                  setSavingClub(false);
                                }
                              }}
                            >
                              {savingClub ? (
                                <Spinner size="sm" />
                              ) : (
                                t("clients.club.actions.subscribe")
                              )}
                            </Button>
                            <Button
                              tag={Link}
                              to="/client-subscription-plans"
                              color="soft-secondary"
                            >
                              {t("clients.club.actions.managePlans")}
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </TabPane>

                  <TabPane tabId="credits">
                    {client ? (
                      <div className="d-flex flex-wrap justify-content-end mb-3">
                        <Button
                          color="primary"
                          size="sm"
                          tag={Link}
                          to={`/orders?action=new&client_id=${client.id}&item_type=package`}
                        >
                          {t("clients.credits.sellPackage")}
                        </Button>
                      </div>
                    ) : null}
                    {loadingCredits ? (
                      <div className="text-center py-4">
                        <Spinner color="primary">{t("clients.loading")}</Spinner>
                      </div>
                    ) : credits.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted mb-2">{t("clients.credits.empty")}</p>
                        <p className="text-muted small mb-3">
                          {t("clients.credits.emptyHint")}
                        </p>
                        {client ? (
                          <Button
                            color="primary"
                            outline
                            size="sm"
                            tag={Link}
                            to={`/orders?action=new&client_id=${client.id}&item_type=package`}
                          >
                            {t("clients.credits.sellPackage")}
                          </Button>
                        ) : null}
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table className="align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{t("clients.credits.table.service")}</th>
                                <th>{t("clients.credits.table.package")}</th>
                                <th>{t("clients.credits.table.remaining")}</th>
                                <th>{t("clients.credits.table.total")}</th>
                                <th>{t("clients.credits.table.purchasedAt")}</th>
                                <th>{t("clients.credits.table.expiresAt")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {credits.map((credit) => (
                                <tr key={credit.id}>
                                  <td>{credit.service?.name ?? notAvailable}</td>
                                  <td>
                                    {credit.source === "loyalty"
                                      ? t("clients.credits.sourceLoyalty")
                                      : credit.service_package?.name ?? notAvailable}
                                  </td>
                                  <td>
                                    <Badge color="soft-success" className="fs-12">
                                      {t("clients.credits.sessionsRemaining", {
                                        count: credit.sessions_remaining,
                                      })}
                                    </Badge>
                                  </td>
                                  <td>{credit.sessions_total}</td>
                                  <td>{formatDate(credit.purchased_at)}</td>
                                  <td>
                                    {credit.expires_at
                                      ? formatDate(credit.expires_at)
                                      : t("clients.credits.noExpiry")}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {creditsMeta && creditsMeta.total > PER_PAGE && (
                          <Row className="align-items-center mt-3">
                            <Col sm={6}>
                              <p className="text-muted mb-0">
                                {t("clients.pagination.showing", {
                                  from: creditsFrom,
                                  to: creditsTo,
                                  total: creditsMeta.total,
                                })}
                              </p>
                            </Col>
                            <Col sm={6}>
                              <Pagination className="pagination-separated justify-content-sm-end mb-0">
                                <PaginationItem disabled={creditsPage <= 1}>
                                  <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (creditsPage > 1) {
                                        setCreditsPage(creditsPage - 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.previous")}
                                  </PaginationLink>
                                </PaginationItem>
                                <PaginationItem
                                  disabled={creditsPage >= creditsLastPage}
                                >
                                  <PaginationLink
                                    next
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (creditsPage < creditsLastPage) {
                                        setCreditsPage(creditsPage + 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.next")}
                                  </PaginationLink>
                                </PaginationItem>
                              </Pagination>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </TabPane>

                  <TabPane tabId="creditStatement">
                    {client ? (
                      <div className="d-flex flex-wrap justify-content-end mb-3">
                        <Button
                          color="primary"
                          size="sm"
                          tag={Link}
                          to={`/orders?action=new&client_id=${client.id}&item_type=package`}
                        >
                          {t("clients.credits.sellPackage")}
                        </Button>
                      </div>
                    ) : null}
                    <Row className="g-2 mb-3">
                      <Col md={4} lg={3}>
                        <Label htmlFor="credit-statement-movement-filter" className="form-label">
                          {t("clients.creditStatement.filter.movement")}
                        </Label>
                        <Input
                          id="credit-statement-movement-filter"
                          type="select"
                          value={movementTypeFilter}
                          onChange={(event) => {
                            setMovementTypeFilter(event.target.value);
                            setTransactionsPage(1);
                          }}
                        >
                          <option value="">
                            {t("clients.creditStatement.filter.allMovements")}
                          </option>
                          {(
                            [
                              "grant",
                              "debit",
                              "expiration",
                              "refund",
                              "adjustment",
                            ] as CreditMovementType[]
                          ).map((movement) => (
                            <option key={movement} value={movement}>
                              {t(`clients.creditStatement.movement.${movement}`)}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={4} lg={3}>
                        <Label htmlFor="credit-statement-from-date" className="form-label">
                          {t("clients.creditStatement.filter.fromDate")}
                        </Label>
                        <Input
                          id="credit-statement-from-date"
                          type="date"
                          value={fromDateFilter}
                          max={toDateFilter || undefined}
                          onChange={(event) => {
                            setFromDateFilter(event.target.value);
                            setTransactionsPage(1);
                          }}
                        />
                      </Col>
                      <Col md={4} lg={3}>
                        <Label htmlFor="credit-statement-to-date" className="form-label">
                          {t("clients.creditStatement.filter.toDate")}
                        </Label>
                        <Input
                          id="credit-statement-to-date"
                          type="date"
                          value={toDateFilter}
                          min={fromDateFilter || undefined}
                          onChange={(event) => {
                            setToDateFilter(event.target.value);
                            setTransactionsPage(1);
                          }}
                        />
                      </Col>
                      {(movementTypeFilter || fromDateFilter || toDateFilter) && (
                        <Col md={4} lg={3} className="d-flex align-items-end">
                          <Button
                            color="soft-secondary"
                            className="w-100"
                            onClick={() => {
                              setMovementTypeFilter("");
                              setFromDateFilter("");
                              setToDateFilter("");
                              setTransactionsPage(1);
                              toast.info(
                                t("clients.creditStatement.filter.cleared")
                              );
                            }}
                          >
                            {t("clients.creditStatement.filter.clear")}
                          </Button>
                        </Col>
                      )}
                    </Row>
                    {loadingTransactions ? (
                      <div className="text-center py-4">
                        <Spinner color="primary">{t("clients.loading")}</Spinner>
                      </div>
                    ) : transactions.length === 0 ? (
                      <div className="text-center py-4">
                        <p className="text-muted mb-0">
                          {movementTypeFilter || fromDateFilter || toDateFilter
                            ? t("clients.creditStatement.emptyFiltered")
                            : t("clients.creditStatement.empty")}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="table-responsive">
                          <Table className="align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>{t("clients.creditStatement.table.date")}</th>
                                <th>{t("clients.creditStatement.table.movement")}</th>
                                <th>{t("clients.creditStatement.table.service")}</th>
                                <th>{t("clients.creditStatement.table.delta")}</th>
                                <th>{t("clients.creditStatement.table.notes")}</th>
                                <th className="text-end">
                                  {t("clients.creditStatement.table.related")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {transactions.map((transaction) => (
                                <tr key={transaction.id}>
                                  <td>{formatDateTime(transaction.created_at)}</td>
                                  <td>
                                    <Badge
                                      color={
                                        transaction.sessions_delta > 0
                                          ? "soft-success"
                                          : transaction.sessions_delta < 0
                                            ? "soft-danger"
                                            : "soft-secondary"
                                      }
                                      className="fw-normal"
                                    >
                                      {resolveMovementLabel(transaction.movement_type)}
                                    </Badge>
                                  </td>
                                  <td>
                                    {transaction.client_credit?.service?.name ??
                                      notAvailable}
                                  </td>
                                  <td
                                    className={
                                      transaction.sessions_delta > 0
                                        ? "text-success"
                                        : transaction.sessions_delta < 0
                                          ? "text-danger"
                                          : undefined
                                    }
                                  >
                                    {formatSessionsDelta(transaction.sessions_delta)}
                                  </td>
                                  <td className="text-muted">
                                    {transaction.notes?.trim() || notAvailable}
                                  </td>
                                  <td className="text-end">
                                    {transaction.order_id ? (
                                      <Link
                                        to={`/orders/${transaction.order_id}`}
                                        className="btn btn-soft-primary btn-sm"
                                      >
                                        {t("clients.creditStatement.actions.openOrder", {
                                          id: transaction.order_id,
                                        })}
                                      </Link>
                                    ) : (
                                      <span className="text-muted" title={t("clients.creditStatement.noRelatedOrderHint")}>
                                        {t("clients.creditStatement.noRelatedOrder")}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>

                        {transactionsMeta && transactionsMeta.total > PER_PAGE && (
                          <Row className="align-items-center mt-3">
                            <Col sm={6}>
                              <p className="text-muted mb-0">
                                {t("clients.pagination.showing", {
                                  from: transactionsFrom,
                                  to: transactionsTo,
                                  total: transactionsMeta.total,
                                })}
                              </p>
                            </Col>
                            <Col sm={6}>
                              <Pagination className="pagination-separated justify-content-sm-end mb-0">
                                <PaginationItem disabled={transactionsPage <= 1}>
                                  <PaginationLink
                                    previous
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (transactionsPage > 1) {
                                        setTransactionsPage(transactionsPage - 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.previous")}
                                  </PaginationLink>
                                </PaginationItem>
                                <PaginationItem
                                  disabled={transactionsPage >= transactionsLastPage}
                                >
                                  <PaginationLink
                                    next
                                    href="#"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      if (transactionsPage < transactionsLastPage) {
                                        setTransactionsPage(transactionsPage + 1);
                                      }
                                    }}
                                  >
                                    {t("clients.pagination.next")}
                                  </PaginationLink>
                                </PaginationItem>
                              </Pagination>
                            </Col>
                          </Row>
                        )}
                      </>
                    )}
                  </TabPane>
                  <TabPane tabId="protocols">
                    {Number.isFinite(parsedClientId) && parsedClientId > 0 ? (
                      <ClientClinicalProtocolsPanel clientId={parsedClientId} />
                    ) : null}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ClientAnamnesisOffcanvas
        isOpen={anamnesisOpen}
        onClose={() => setAnamnesisOpen(false)}
        clientId={client?.id ?? 0}
        clientName={client?.name ?? ""}
      />

      <ClientMergeModal
        isOpen={mergeModalOpen}
        initialKeepId={client?.id ?? null}
        onClose={() => setMergeModalOpen(false)}
        onMerged={() => {
          if (Number.isFinite(parsedClientId) && parsedClientId > 0) {
            void getClientById(parsedClientId)
              .then((response) => setClient(response.data))
              .catch(() => undefined);
          }
        }}
      />

      <Modal
        isOpen={deleteModalOpen}
        toggle={() => {
          if (!deletingClient) {
            setDeleteModalOpen(false);
          }
        }}
        centered
      >
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" aria-hidden="true" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("clients.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {client
                  ? t("clients.delete.description", { name: client.name })
                  : t("clients.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button
              color="light"
              disabled={deletingClient}
              onClick={() => setDeleteModalOpen(false)}
            >
              {t("clients.actions.cancel")}
            </Button>
            <Button
              color="danger"
              disabled={deletingClient}
              onClick={() => void confirmDeleteClient()}
            >
              {deletingClient ? (
                <Spinner size="sm" className="me-1" />
              ) : null}
              {t("clients.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ClientDetail;
